import React, { useState, useEffect } from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { SelectBox } from '@tecuity/granite-ui/SelectBox.js'
import { ContextBar } from '@tecuity/granite-ui/ContextBar.js'
import { ContextButton } from '@tecuity/granite-ui/ContextButton.js'
import DummyApostilleDetails from './DummyApostilleDetails'
import { Body, Drawer } from 'components/Shell/Shell'
import { Spinner } from 'components/Loaders/Loaders'
import Revert from '@tecuity/sos-icons/react/revert'
import RightArrowIcon from '@tecuity/sos-icons/react/right-arrow'
import * as api from '../../../requests.js'
import {
  SET_SOURCE_TYPE_DETAIL_CONFIG,
  SET_DETAIL_CONFIG_SOURCE_TYPE,
  SET_DETAIL_CONFIG_SUB_TYPE,
  SET_SELECTED_DETAIL_OUTPUT,
  SET_SETTINGS_RECORD_DETAIL_DRAWER,
} from '../SettingsReducer.js'
import './RecordDetail.scss'
import AttributeDrawer, { GenericAttributeDrawer } from './AttributeDrawer.js'
import { setErrorToast, setSuccessToast } from '../../../actions.js'
import DummyUccDetails from './DummyUccDetails'
import GenericDummyDetails from './GenericDummyDetails' //, { dummyTest }
import MiscRecordDetailConfig from '../MiscRecords/MiscRecordDetailConfig'
import { FilingSourceTypeIds } from '../../../Utilities/Utilities'
import { SUBROUTES } from '../../../App'

const RecordDetails = () => {
  const dispatch = useDispatch()
  const {
    recordDetailConfig: {
      isDirty,
      sourceType,
      sourceTypeConfig,
      historyConfig,
      drawerShown,
      selectedDetailOutput,
      subType,
    },
  } = useSelector((state) => state.settings, shallowEqual)
  const { isSidebarCollapsed } = useSelector((state) => state.app, shallowEqual)
  const allModules = (SUBROUTES || [])
    .filter((r) => r.IsMiscRecordsType)
    .flatMap((r) => r.MiscRecordsModules || [])
  const moduleOptions = allModules.map((m) => ({
    value: m.MiscellaneousModuleId,
    label: m.DisplayName,
  }))
  const [selectedModuleId, setSelectedModuleId] = useState(
    allModules[0]?.MiscellaneousModuleId ?? null,
  )
  const [isMiscDirty, setIsMiscDirty] = useState(false)

  const [sourceTypeOptions, setSourceTypeOptions] = useState([])
  const [subTypeOptions, setSubTypeOptions] = useState([])
  const [subTypeLabel, setSubTypeLabel] = useState('')
  const [loading, setLoading] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const [isStructureCodeStandard, setIsStructureCodeStandard] = useState(false)

  useEffect(() => {
    setLoading(true)
    api.GET_SOURCE_TYPE_WITH_EDITABLE_RECORD_DETAIL(
      (res) => {
        if (res) {
          const options = [
            ...res
              .map((t) => ({
                value: t.Id,
                label: t.Name.trim(),
                moduleName: t.ModuleName,
              }))
              .sort((a, b) => (a.label > b.label ? 1 : -1)),
          ]
          setSourceTypeOptions(options)
          dispatch({ type: SET_DETAIL_CONFIG_SOURCE_TYPE, sourceType: options[0].value.toString() })
          setLoading(false)
        }
      },
      (err) => {
        console.error(err.message)
        setLoading(false)
      },
    )
  }, [])

  const getConfig = (sourceType) => {
    setLoading(true)
    setIsStructureCodeStandard(false)
    dispatch({ type: SET_SETTINGS_RECORD_DETAIL_DRAWER, drawerShown: false })
    dispatch({
      type: SET_SOURCE_TYPE_DETAIL_CONFIG,
      sourceTypeConfig: {},
      historyConfig: {},
    })
    switch (sourceType) {
      case '54': //Business BaseRecord
        api.FETCH_BUSINESS_ENTITY_RECORD_DETAIL_SETTINGS(
          '1',
          (res) => {
            dispatch({
              type: SET_SOURCE_TYPE_DETAIL_CONFIG,
              sourceTypeConfig: res,
            })
            setLoading(false)
          },
          (err) => {
            console.error(err)
            setLoading(false)
          },
        )
        break
      case '720': //Apostille
        api.GET_APOSTILE_RECORD_DETAIL_SETTINGS(
          (res) => {
            dispatch({
              type: SET_SOURCE_TYPE_DETAIL_CONFIG,
              sourceTypeConfig: res,
              historyConfig: res.RecordDetailHistorySettings || {},
            })
            setLoading(false)
          },
          (err) => {
            console.error(err)
            setLoading(false)
          },
        )
        break
      case '852': //Facsimile
        setLoading(false)
        break
      case '850': //Notary
        api.GET_NOTARY_RECORD_DETAIL_SETTINGS(
          (res) => {
            dispatch({
              type: SET_SOURCE_TYPE_DETAIL_CONFIG,
              sourceTypeConfig: res,
              // historyConfig: res.HitoryConfig
            })
            setLoading(false)
          },
          (err) => {
            console.error(err)
            setLoading(false)
          },
        )
        break
      case '2000': // UCC Filing
        api.GET_UCC_FILING_DETAIL_SETTINGS(
          (res) => {
            dispatch({
              type: SET_SOURCE_TYPE_DETAIL_CONFIG,
              sourceTypeConfig: res,
              historyConfig: res.RecordDetailHistorySettings || {},
            })
            setLoading(false)
          },
          (err) => {
            console.error(err)
            setLoading(false)
          },
        )
        break
      case '2001': // UCC Info Request
        api.GET_UCC_FILING_INFO_REQUESET_DETAIL_SETTINGS(
          (res) => {
            dispatch({
              type: SET_SOURCE_TYPE_DETAIL_CONFIG,
              sourceTypeConfig: res,
            })
            setLoading(false)
          },
          (err) => {
            console.error(err)
            setLoading(false)
          },
        )
        break
      case '869': // Data Request
        api.GET_DATA_REQUEST_DETAIL_SETTINGS(
          (res) => {
            dispatch({
              type: SET_SOURCE_TYPE_DETAIL_CONFIG,
              sourceTypeConfig: res,
            })
            setLoading(false)
          },
          (err) => {
            console.error(err)
            setLoading(false)
          },
        )
        break
      case '157': // Registered Agent
        api.GET_RECORD_DETAIL_SETTING_BY_TYPE(
          'RegisteredAgentRecordDetail',
          (res) => {
            dispatch({
              type: SET_SOURCE_TYPE_DETAIL_CONFIG,
              sourceTypeConfig: res,
            })
            setLoading(false)
          },
          (err) => {
            console.error(err)
            setLoading(false)
          },
        )
        break
      case '810': // Trademark
        api.GET_RECORD_DETAIL_SETTING_BY_TYPE(
          'TrademarkRecordDetail',
          (res) => {
            dispatch({
              type: SET_SOURCE_TYPE_DETAIL_CONFIG,
              sourceTypeConfig: res,
            })
            setLoading(false)
          },
          (err) => {
            console.error(err)
            setLoading(false)
          },
        )
        break
      case '1300': // Charity
        api.GET_RECORD_DETAIL_SETTING_BY_TYPE(
          'CharityRecordDetails',
          (res) => {
            setIsStructureCodeStandard(true)
            dispatch({
              type: SET_SOURCE_TYPE_DETAIL_CONFIG,
              sourceTypeConfig: res,
            })
            setLoading(false)
          },
          (err) => {
            console.error(err)
            setLoading(false)
          },
        )
        break
      case '1400': // Contractor
        api.GET_RECORD_DETAIL_SETTING_BY_TYPE(
          'ContractorRecordDetails',
          (res) => {
            setIsStructureCodeStandard(true)
            dispatch({
              type: SET_SOURCE_TYPE_DETAIL_CONFIG,
              sourceTypeConfig: res,
            })
            setLoading(false)
          },
          (err) => {
            console.error(err)
            setLoading(false)
          },
        )
        break
      case '1500': // Lobbyist
        api.GET_RECORD_DETAIL_SETTING_BY_TYPE(
          'LobbyistRecordDetails',
          (res) => {
            setIsStructureCodeStandard(true)
            dispatch({
              type: SET_SOURCE_TYPE_DETAIL_CONFIG,
              sourceTypeConfig: res,
            })
            setLoading(false)
          },
          (err) => {
            console.error(err)
            setLoading(false)
          },
        )
        break
      default:
        setLoading(false)
    }
  }

  useEffect(() => {
    getConfig(sourceType)
  }, [sourceType])

  const onSaveSuccess = (res) => {
    // console.log(res)
    getConfig(sourceType)
    setSaveLoading(false)
    dispatch(setSuccessToast('Record Detail Config Saved'))
  }

  const onSaveError = (err) => {
    console.error(err)
    setSaveLoading(false)
    dispatch(setErrorToast('Failed to Save Record Detail Config'))
  }

  const saveConfig = () => {
    setSaveLoading(true)
    switch (sourceType) {
      case '54':
        api.SET_BUSINESS_ENTITY_RECORD_DETAIL_SETTINGS(
          { ...sourceTypeConfig },
          onSaveSuccess,
          onSaveError,
        )
        break
      case '720': //Apostille
        api.SET_APOSTILE_RECORD_DETAIL_SETTINGS(
          { ...sourceTypeConfig, RecordDetailHistorySettings: { ...historyConfig } },
          onSaveSuccess,
          onSaveError,
        )
        break
      case '852': //Facsimile
        setSaveLoading(false)
        break
      case '850': //Notary
        api.SET_NOTARY_RECORD_DETAIL_SETTINGS({ ...sourceTypeConfig }, onSaveSuccess, onSaveError)
        break
      case '2000': //UCC
        api.SET_UCC_FILING_DETAIL_SETTINGS({ ...sourceTypeConfig }, onSaveSuccess, onSaveError)
        break
      case '2001': //UCC Info Request
        api.SET_UCC_FILING_INFO_REQUESET_DETAIL_SETTINGS(
          { ...sourceTypeConfig },
          onSaveSuccess,
          onSaveError,
        )
        break
      case '869': // Data Request
        api.SET_DATA_REQUEST_DETAIL_SETTINGS({ ...sourceTypeConfig }, onSaveSuccess, onSaveError)
        break
      case '157': // Registered Agent
        api.SET_RECORD_DETAIL_SETTING_BY_TYPE(
          'RegisteredAgentRecordDetail',
          { ...sourceTypeConfig },
          onSaveSuccess,
          onSaveError,
        )
        break
      case '810': // Trademark
        api.SET_RECORD_DETAIL_SETTING_BY_TYPE(
          'TrademarkRecordDetail',
          { ...sourceTypeConfig },
          onSaveSuccess,
          onSaveError,
        )
        break
      case '1300': // Charity
        api.SET_RECORD_DETAIL_SETTING_BY_TYPE(
          'CharityRecordDetails',
          { ...sourceTypeConfig },
          onSaveSuccess,
          onSaveError,
        )
        break
      case '1400': // Contractor
        api.SET_RECORD_DETAIL_SETTING_BY_TYPE(
          'ContractorRecordDetails',
          { ...sourceTypeConfig },
          onSaveSuccess,
          onSaveError,
        )
        break
      case '1500': // Lobbyist
        api.SET_RECORD_DETAIL_SETTING_BY_TYPE(
          'LobbyistRecordDetails',
          { ...sourceTypeConfig },
          onSaveSuccess,
          onSaveError,
        )
        break
      default:
        setSaveLoading(false)
    }
  }

  const getSubTypeOptions = () => {
    switch (sourceType) {
      case '54': //business
        setSubTypeLabel('Record Type')
        if (Object.keys(sourceTypeConfig).length > 1 && subTypeOptions.length === 0) {
          const configKeys = new Set(Object.keys(sourceTypeConfig).map(Number))
          api.GET_ALL_BASIC_LOOKUP_TYPES(
            'BusinessRecordType',
            (types) => {
              const specificOptions = types
                .filter((t) => configKeys.has(t.Id))
                .map((t) => ({ value: String(t.Id), label: t.Name }))
              const options = [
                ...(configKeys.has(0) ? [{ value: '0', label: 'All' }] : []),
                ...specificOptions,
              ]
              setSubTypeOptions(options)
              if (options.length > 0) {
                dispatch({ type: SET_DETAIL_CONFIG_SUB_TYPE, subType: Number(options[0].value) })
              }
            },
            console.error,
          )
        }
        break
      default:
        setSubTypeOptions([])
        setSubTypeLabel('')
        break
    }
  }

  useEffect(() => {
    getSubTypeOptions()
  }, [sourceType, sourceTypeConfig])

  const isMiscRecords = sourceType === String(FilingSourceTypeIds.Miscellaneous)

  useEffect(() => {
    if (!isMiscRecords) setIsMiscDirty(false)
  }, [isMiscRecords])

  if (isMiscRecords) {
    return (
      <Body showDrawer={drawerShown}>
        <div className="settings-list-view misc-record-detail-settings">
          <h2>Record Detail Configuration</h2>
          <div className="custom-correspondence-options">
            <SelectBox
              label="Source Type"
              noPlaceholder
              value={sourceType}
              onChange={(val) => {
                dispatch({ type: SET_DETAIL_CONFIG_SOURCE_TYPE, sourceType: val })
              }}
              options={sourceTypeOptions}
              disabled={isMiscDirty ? true : undefined}
              description={isMiscDirty ? 'Save or Revert changes to modify.' : undefined}
            />
            <SelectBox
              label="Module"
              noPlaceholder
              value={selectedModuleId || ''}
              onChange={(val) => setSelectedModuleId(+val)}
              options={moduleOptions}
              disabled={isMiscDirty ? true : undefined}
              description={isMiscDirty ? 'Save or Revert changes to modify.' : undefined}
            />
          </div>
          <MiscRecordDetailConfig
            selectedModuleId={selectedModuleId}
            onDirtyChange={(val) => setIsMiscDirty(val)}
          />
        </div>
      </Body>
    )
  }

  return (
    <Body showDrawer={drawerShown}>
      <div className="settings-list-view">
        <h2>Record Detail Configuration</h2>
        <div className="custom-correspondence-options">
          <SelectBox
            label="Source Type"
            noPlaceholder
            value={sourceType}
            onChange={(val) => {
              dispatch({ type: SET_DETAIL_CONFIG_SOURCE_TYPE, sourceType: val })
            }}
            options={sourceTypeOptions}
          />
          {subTypeOptions.length > 0 && (
            <SelectBox
              label={subTypeLabel}
              noPlaceholder
              value={subType.toString()}
              onChange={(val) => {
                dispatch({ type: SET_DETAIL_CONFIG_SUB_TYPE, subType: Number(val) })
              }}
              options={subTypeOptions}
            />
          )}
        </div>
        {loading ? (
          <Spinner size="medium" themeColor="dark-neutral" />
        ) : (
          <div>
            {sourceType === '720' && <DummyApostilleDetails />}
            {sourceType === '852' && <DummyFacsimileDetails />}
            {sourceType === '2000' && <DummyUccDetails />}
            {![720, 852, 2000].includes(Number(sourceType)) && <GenericDummyDetails />}
          </div>
        )}
        <div
          className={`context-bar-wrapper ${drawerShown ? 'drawer-opened' : ''} ${
            isSidebarCollapsed ? 'sidebar-collapsed' : ''
          }`}
        >
          <ContextBar>
            <div className="context-button-wrapper">
              <ContextButton
                variant="danger"
                onClick={() => {
                  getConfig(sourceType)
                }}
                disabled={!isDirty}
                leftIcon={
                  <div className="checkmark-wrapper left">
                    <Revert />
                  </div>
                }
              >
                Revert
              </ContextButton>
              <ContextButton
                onClick={saveConfig}
                variant="primary"
                disabled={!isDirty}
                rightIcon={
                  <div className="checkmark-wrapper right">
                    <RightArrowIcon />
                  </div>
                }
              >
                Save
              </ContextButton>
              {saveLoading && (
                <div style={{ marginTop: '20px', marginLeft: '20px', marginRight: '20px' }}>
                  <Spinner
                    size="small"
                    themeColor="dark-neutral"
                    className="record-detail-loader"
                  />
                </div>
              )}
            </div>
          </ContextBar>
        </div>
      </div>
      <Drawer
        showCloseButton
        show={drawerShown}
        title={'Edit Output'}
        subtitle={selectedDetailOutput.accessor || 'Not Selected'}
        onSidebarDismiss={() => {
          dispatch({ type: SET_SELECTED_DETAIL_OUTPUT, accessor: '', info: {}, drawerShown: false })
        }}
      >
        {selectedDetailOutput.isGeneric ? (
          <GenericAttributeDrawer isStructureCodeStandard={isStructureCodeStandard} />
        ) : (
          <AttributeDrawer />
        )}
      </Drawer>
    </Body>
  )
}

export default RecordDetails

const DummyFacsimileDetails = () => {
  return <div>No Record Detail page to configure</div>
}
